import {
  typedCollection,
  getCollection,
  orderBy,
  where,
} from '@/lib/firebase/firestore'
import { submitServiceRequestFn } from '@/lib/firebase/functions'
import type {
  ServiceCategoryDocument,
  ServiceDocument,
  CatalogAddonDocument,
  ServiceRequestDocument,
  PriceUnit,
} from '@/types'
import type { CatalogData } from '../types'

const categoriesCol = () => typedCollection<ServiceCategoryDocument>('serviceCategories')
const servicesCol = () => typedCollection<ServiceDocument>('services')
const catalogAddonsCol = () => typedCollection<CatalogAddonDocument>('catalogAddons')

function derivePriceUnit(pricingType: string): PriceUnit | undefined {
  switch (pricingType) {
    case 'flat':        return 'flat'
    case 'hourly':      return 'perHour'
    case 'perMinute':   return 'perMinute'
    case 'perHeadshot': return 'perHeadshot'
    default:            return undefined
  }
}

export const requestService = {
  async getCatalog(): Promise<CatalogData> {
    const [allCategories, allServices, addons] = await Promise.all([
      getCollection(categoriesCol(), orderBy('order', 'asc')),
      getCollection(servicesCol(), where('isPublished', '==', true), orderBy('order', 'asc')),
      getCollection(catalogAddonsCol()),
    ])
    // Derive priceUnit and basePrice from pricingType/price when not explicitly stored
    const hydrated = allServices.map((s) => ({
      ...s,
      priceUnit: s.priceUnit ?? derivePriceUnit(s.pricingType),
      basePrice: s.basePrice ?? s.price,
    }))
    const offerings = hydrated   // all published services are offerings
    // Show every category that has at least one published offering — matched
    // either by serviceKey (the canonical link) or by the offering's category
    // title (covers services that don't have serviceKey set on the doc).
    const activeServiceKeys = new Set(offerings.map((o) => o.serviceKey).filter(Boolean))
    const activeCategoryTitles = new Set(
      offerings.map((o) => o.category.toLowerCase())
    )
    const services = allCategories.filter(
      (c) =>
        activeServiceKeys.has(c.serviceKey) ||
        activeCategoryTitles.has(c.title.toLowerCase())
    )
    return { services, allCategories, offerings, addons }
  },

  async submitRequest(
    payload: Omit<ServiceRequestDocument, 'id' | 'createdAt' | 'updatedAt' | 'status'>
  ): Promise<string> {
    // Status is stamped server-side; the newsletter opt-in side-effect is
    // handled server-side too, so callers no longer need to chain it.
    const res = await submitServiceRequestFn({
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      organizationName: payload.organizationName,
      discountCategory: payload.discountCategory,
      discountAppliedPercent: payload.discountAppliedPercent,
      mailingOptIn: payload.mailingOptIn,
      services: payload.services,
      quoteTotal: payload.quoteTotal,
      additionalInfo: payload.additionalInfo,
      requestedProviders: payload.requestedProviders,
      tosAccepted: payload.tosAccepted,
      bookingAcknowledge: payload.bookingAcknowledge,
    })
    return res.data.requestId
  },
}
